import { useNavigate } from 'react-router-dom';
import { Button, Badge, Spinner } from '@/components/ui';
import { ProductLookupResultCard } from '@/components/scanner/ProductLookupResult';
import { Package, X } from 'lucide-react';
import type { ProductLookupResult } from '@/services/openFoodFactsService';

interface ScanResult {
  barcode: string;
  lookupResult: ProductLookupResult | null;
  existingProduct: {
    nom: string;
    categorie: string;
  } | null;
}

interface ScanResultModalProps {
  isLookingUp: boolean;
  scanResult: ScanResult | null;
  onClose: () => void;
  onOpenScanner: () => void;
}

export function ScanResultModal({
  isLookingUp,
  scanResult,
  onClose,
  onOpenScanner,
}: ScanResultModalProps) {
  const navigate = useNavigate();

  if (!isLookingUp && !scanResult) {
    return null;
  }

  const handleGoToProduct = () => {
    navigate('/inventory');
    onClose();
  };

  const handleCreateProduct = () => {
    navigate('/products');
    onClose();
  };

  const handleScanAgain = () => {
    onClose();
    onOpenScanner();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Résultat du scan
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4">
          {isLookingUp && (
            <div className="text-center py-8">
              <Spinner centered label="Recherche du produit..." />
            </div>
          )}

          {!isLookingUp && scanResult?.existingProduct && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Package className="text-green-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-green-900">
                      Produit trouvé dans votre catalogue
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {scanResult.existingProduct.nom}
                    </p>
                    <Badge variant="success" size="sm" className="mt-2">
                      {scanResult.existingProduct.categorie}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Fermer
                </Button>
                <Button onClick={handleGoToProduct} className="flex-1">
                  Voir le stock
                </Button>
              </div>
            </div>
          )}

          {!isLookingUp && scanResult?.lookupResult && (
            <div className="space-y-4">
              {scanResult.lookupResult.found ? (
                <ProductLookupResultCard
                  result={scanResult.lookupResult}
                  isLoading={false}
                  onUseProduct={() => {
                    navigate('/products');
                    onClose();
                  }}
                  onManualEntry={() => {
                    navigate('/products');
                    onClose();
                  }}
                  onScanAgain={handleScanAgain}
                />
              ) : (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="font-medium text-yellow-900">Produit non trouvé</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Le code-barres "{scanResult.barcode}" n'a pas été trouvé dans la
                      base de données Open Food Facts.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleScanAgain} className="flex-1">
                      Scanner à nouveau
                    </Button>
                    <Button onClick={handleCreateProduct} className="flex-1">
                      Créer manuellement
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
